import React, { useState, useRef, useEffect } from "react";
import LabelInput from "../common/forms/Labelinput";
import { TrendingUp } from "lucide-react";
import Button from "../common/ui/button";
import CancelButton from "../common/ui/cancelbutton";
import DateLabelInput from "../common/forms/DateLabelInput";
import NoFound from "../common/ui/NoFound";
import toast from "react-hot-toast";
import { MdEditSquare } from "react-icons/md";
import { RiDeleteBin5Line } from "react-icons/ri";
import { ChevronDown, Search } from "lucide-react";
import { createPortal } from "react-dom";
import { DeletePreviewModal, FinalDeleteConfirmModal } from "../common/ui/deletemodal";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import WarningModal from "../common/ui/warningmodel";

const SelectMenu = ({
  label,
  options = [],
  value,
  onChange,
  searchable = false,
  disabled = false,
  usePortal = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({});

  // ---- Close on outside click ----
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---- Portal positioning ----
  useEffect(() => {
    if (isOpen && usePortal && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuStyle({
        position: "absolute",
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 9999,
      });
    }
  }, [isOpen, usePortal]);

  // ---- Filter options ----
  const filteredOptions = searchable
    ? options.filter((o) =>
      o.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : options;

  // ---- Display value ----
  const getDisplayValue = () => {
    if (!value) return `Select ${label}`;
    const found = options.find((o) => String(o.value) === String(value));
    return found ? found.label : `Select ${label}`;
  };

  // ---- Select ----
  const handleSelect = (opt) => {
    onChange(opt.value);
    setIsOpen(false);
  };

  // ---- Dropdown ----
  const menuContent = (
    <div
      ref={dropdownRef}
      className="z-50 bg-white border rounded shadow-2xl max-h-64 overflow-hidden animate-in slide-in-from-top-2 duration-200"
      style={usePortal ? menuStyle : {}}
    >
      {/* Search */}
      {searchable && (
        <div className="p-3 border-b border-slate-100 bg-slate-50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder={`Search ${label.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-transparent"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Options */}
      <div className="py-1 max-h-52 overflow-y-auto">
        {filteredOptions.length === 0 ? (
          <div className="px-4 py-3 text-sm text-center text-slate-400">
            No {label.toLowerCase()} available
          </div>
        ) : (
          filteredOptions.map((opt) => (
            <div
              key={opt.value}
              className={`px-4 py-3 cursor-pointer text-sm hover:bg-slate-50 flex items-center transition-colors ${String(value) === String(opt.value)
                ? "bg-sidebarHover text-primary hover:bg-[rgba(255,48,48,0.16)] border-r-2 border-primary"
                : "text-slate-700"
                }`}
              onClick={() => handleSelect(opt)}
            >
              <span className="font-medium">{opt.label}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="relative group">
      {/* MAIN BUTTON */}
      <div
        ref={buttonRef}
        className={`w-full px-2 sm:px-3 py-2 border-2 rounded bg-white flex items-center justify-between min-h-[36px] sm:min-h-[44px] transition-all duration-200 ${disabled
          ? "border-slate-200 bg-slate-50 cursor-not-allowed opacity-60"
          : isOpen
            ? "shadow-lg cursor-pointer"
            : "border-slate-200 hover:border-slate-300 hover:shadow-md cursor-pointer"
          }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span
          className={`text-sm ${disabled ? "text-slate-400" : "text-slate-700"
            } truncate mr-2`}
        >
          {getDisplayValue()}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${disabled ? "text-slate-300" : "text-slate-400"
            } ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {/* DROPDOWN */}
      {isOpen &&
        !disabled &&
        (usePortal
          ? createPortal(menuContent, document.body)
          : (
            <div className="absolute w-full mt-2">{menuContent}</div>
          ))}
    </div>
  );
};


export default function EditInbondentryform({
  biNumberList = [],
  loadingBINumber = false,
  materialList = [],
  loadingMaterials = false,
  formData = {},
  updateFormData,
  childrecords = [],
  setchildRecords,
  masterrecords = [],
  setmasterRecords,
  isChildEdit = true,
  selectedchildId,
  selectedparentId
}) {
  const [editIndex, setEditIndex] = useState(null);
  const [pendingDeleteData, setPendingDeleteData] = useState(null);
  const [deleteMode, setDeleteMode] = useState(null); // "child"
  const [showDeletePreview, setShowDeletePreview] = useState(false);
  const [showFinalDeleteConfirm, setShowFinalDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");


  // === Helpers - Enhanced to handle all edge cases ===
  const safePrimitive = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "string" || typeof val === "number") return val;
    if (typeof val === "object") {
      if ("value" in val) return val.value ?? "";
      if ("label" in val) return val.label ?? "";
    }
    return String(val);
  };

  const getShortCode = (id) => {
    const found = materialList.find(m => String(m.value) === String(id));
    return found ? found.label : "-";
  };

  const toNumber = (v, fallback = 0) => {
    if (v === null || v === undefined || v === "") return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const handleDeleteChild = (child) => {
    setPendingDeleteData({ child });
    setDeleteMode("child");
    setShowDeletePreview(true);
    setShowFinalDeleteConfirm(false);
  };
  const handleCancelPreview = () => {
    setShowDeletePreview(false);
    setPendingDeleteData(null);
    setDeleteMode(null);
  };
  const handleProceedToFinalConfirm = () => {
    setShowDeletePreview(false);
    setShowFinalDeleteConfirm(true);
  };
  const handleConfirmDelete = async () => {
    if (!pendingDeleteData?.child) return;

    const child = pendingDeleteData.child;

    // Check if this is a new entry (local only)
    const isNewRecord = !child.inbond_child_id && !child.inbond_child_master_id && !child.id;

    if (isNewRecord) {
      // Remove **only this new record** from local state
      setchildRecords(prev => prev.filter(c => c.tempId !== child.tempId));
      toast.success("New entry removed successfully");
    } else {
      // Existing record → call API
      try {
        setLoading(true);
        const childId = child.inbond_child_id || child.inbond_child_master_id || child.id;

        let response = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.SOFT_DELETE_PARTIAL(childId)}`,
          { method: "PUT", headers: getHeaders(true) }
        );

        // -------------------------
        // 🔥 If ACCESS TOKEN expired
        // -------------------------
        if (response.status === 401) {
          console.warn("Access token expired. Attempting refresh...");

          const newToken = await refreshAccessToken();

          if (newToken) {
            // Retry API call with new token
            response = await fetch(
              `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.SOFT_DELETE_PARTIAL(childId)}`,
              { method: "PUT", headers: getHeaders(true) }
            );
          } else {
            console.error("Refresh token failed. Logging out...");
            logout(); // redirect user
            return;
          }
        }


        const result = await response.json().catch(() => ({}));

        if (response.ok && result.status === 200) {
          toast.success(result.message || "Child entry deleted successfully");

          // Remove only the deleted record
          setchildRecords(prev => prev.filter(c => c.id !== childId));
          navigate("/in-bond");
        } else {
          setWarningMessage(result.message || "Failed to delete child entry");
          setWarningVisible(true);
        }
      } catch (err) {
        console.error(err);
        setWarningMessage("Unexpected error while deleting entry");
        setWarningVisible(true);
      } finally {
        setLoading(false);
      }
    }

    // Reset modals
    setShowFinalDeleteConfirm(false);
    setShowDeletePreview(false);
    setPendingDeleteData(null);
    setDeleteMode(null);
  };






  // === Date utilities ===
  const normalizeDate = (dateInput) => {
    if (!dateInput) return "";
    if (dateInput instanceof Date) {
      const yyyy = dateInput.getFullYear();
      const mm = String(dateInput.getMonth() + 1).padStart(2, "0");
      const dd = String(dateInput.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
    const s = String(dateInput).trim();
    if (s.includes("/")) {
      const parts = s.split("/");
      if (parts.length === 3) {
        return `${parts[2].padStart(4, "0")}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
      }
    }
    if (s.includes("-")) {
      const parts = s.split("-");
      if (parts.length === 3 && parts[0].length === 2) {
        return `${parts[2].padStart(4, "0")}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
      }
      return s;
    }
    return s;
  };

  const compareDates = (d1, d2) => {
    if (!d1 || !d2) return 0;
    const date1 = new Date(`${normalizeDate(d1)}T00:00:00`);
    const date2 = new Date(`${normalizeDate(d2)}T00:00:00`);
    if (!isFinite(date1.getTime()) || !isFinite(date2.getTime())) return 0;
    if (date1.getTime() < date2.getTime()) return -1;
    if (date1.getTime() > date2.getTime()) return 1;
    return 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(normalizeDate(dateStr));
    if (!isFinite(d.getTime())) return String(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${day} ${monthNames[d.getMonth()]} ${d.getFullYear()}`;
  };

  // === Form field handlers ===
  const handleBiChange = (e) => {
    const raw = e?.target?.value || "";
    const val = raw.replace(/^BI\s?/i, "").trim();
    updateFormData("biNumber", val);
  };

  const handleBeNumberChange = (e) => {
    updateFormData("beNumber", (e?.target?.value || "").trim());
  };

  const handleBeDateChange = (e) => {
    const val = e?.target?.value || "";
    updateFormData("beDate", normalizeDate(val));
  };

  const handleInbondDateChange = (e) => {
    const val = e?.target?.value || "";
    const inbondDate = normalizeDate(val);
    if (formData.beDate && inbondDate) {
      const cmp = compareDates(inbondDate, formData.beDate);
      if (cmp < 0) {
        setWarningMessage("In-bond Date cannot be before BE Date.");
        setWarningVisible(true);
        return;
      }
    }
    updateFormData("inbondDate", inbondDate);
  };

  const makeNumericHandler = (key) => (e) => {
    const raw = e?.target?.value;
    updateFormData(key, raw === "" ? "" : raw);
  };


  // Convert childrecords dictionary to array if needed
  const childArray = Array.isArray(childrecords) ? childrecords : Object.values(childrecords);

  // === Totals from child records ===
  const totalDuty = childArray.reduce(
    (sum, r) => sum + toNumber(r.duty_inbond_amount_inr, 0),
    0
  );

  const totalWeight = childArray.reduce(
    (sum, r) => sum + toNumber(r.weight, 0),
    0
  );

  const totalAssessment = childArray.reduce(
    (sum, r) => sum + toNumber(r.assessment_amount_inr, 0),
    0
  );

  const totalAmount = childArray.reduce(
    (sum, r) => sum + toNumber(r.material_amount_usd, 0),
    0
  );

  // On page load or when selectedchildId changes
  useEffect(() => {
    if (selectedchildId !== "" && selectedchildId !== 0 && childrecords.length > 0) {
      const entryIndex = childrecords.findIndex(record => String(record.id) === String(selectedchildId));
      if (entryIndex !== -1) {
        const entry = childrecords[entryIndex];

        // Populate form data
        updateFormData("material", String(entry.material_master_id || ""));
        updateFormData("weight", String(entry.weight || ""));
        updateFormData("invoiceValue", String(entry.invoice_amount_usd || ""));
        updateFormData("dollar", String(entry.dollar_inr || ""));
        updateFormData("price", String(entry.price || ""));
        updateFormData("duty", String(entry.duty_inbond_amount_inr || ""));
        updateFormData("assessment", String(entry.assessment_amount_inr || ""));
        updateFormData("totalAmount", String(entry.material_amount_usd || ""));
        updateFormData("id", String(entry.id || ""));

        // Set edit index so the form is in "edit mode"
        setEditIndex(entryIndex);
      }
    }
  }, [selectedchildId, childrecords]);


  // === Validation ===
  const canAddEntry = (() => {
    // If a child is selected for edit, prevent adding a new entry


    const biNumber = safePrimitive(formData.biNumber);
    const beNumber = safePrimitive(formData.beNumber);
    const beDate = safePrimitive(formData.beDate);
    const inbondDate = safePrimitive(formData.inbondDate);
    const material = safePrimitive(formData.material);
    const weight = formData.weight;
    const invoiceValue = formData.invoiceValue;
    const duty = formData.duty;
    const assessment = formData.assessment;

    if (!biNumber || !beNumber || !beDate || !inbondDate || !material || !weight || !invoiceValue || !duty || !assessment)
      return false;

    if (compareDates(inbondDate, beDate) < 0) return false;

    return true;
  })();



  // === Add or Update entry ===
  // === Add or Update entry ===
  const handleAddOrUpdateEntry = async () => {
    const materialId = safePrimitive(formData.material);
    const weight = toNumber(formData.weight, 0);
    const childId = formData.id;

    if (!materialId || !weight) {
      setWarningMessage("Please fill all required fields");
      setWarningVisible(true);
      return;
    }

    try {
      setLoading(true);

      // Only validate weight if editing an existing entry
      if (childId && editIndex !== null) {
        // Build weight validation URL
        const weightCheckUrl =
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.GET_INBOND_EDIT_ENTRY_DETAILS()}` +
          `?inbond_child_id=${childId}&inbond_weight=${weight}`;

        // Call weight validation API
        let response = await fetch(
          weightCheckUrl,
          { method: "GET", headers: getHeaders(true) }
        );

        // Handle token expiry
        if (response.status === 401) {
          console.warn("Access token expired. Attempting refresh...");
          const newToken = await refreshAccessToken();
          if (newToken) {
            response = await fetch(
              weightCheckUrl,
              { method: "GET", headers: getHeaders(true) }
            );
          } else {
            console.error("Refresh token failed. Logging out...");
            logout();
            return;
          }
        }

        const result = await response.json().catch(() => ({}));

        // Check if weight update is allowed
        if (!response.ok || result.status !== 200) {
          setWarningMessage(result.message || "Cannot update weight. Please check the value.");
          setWarningVisible(true);
          setLoading(false);
          return;
        }
      }

      // ✅ Weight validation passed (or new entry), proceed to add/update locally
      const materialObj = materialList.find((m) => String(m.value) === String(materialId)) || null;
      const newEntry = {
        id: toNumber(formData.id),
        material_master_id: materialId,
        material_name: materialObj?.label || "",
        weight: weight,
        invoice_amount_usd: toNumber(formData.invoiceValue, 0),
        dollar_inr: toNumber(formData.dollar, 0),
        price: toNumber(formData.price, 0),
        duty_inbond_amount_inr: toNumber(formData.duty, 0),
        assessment_amount_inr: toNumber(formData.assessment, 0),
        material_amount_usd: weight * toNumber(formData.invoiceValue, 0),
      };

      if (editIndex === null && (!selectedchildId || selectedchildId === 0)) {
        // Adding new entry
        const id = crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
        setchildRecords([...childrecords, { id, ...newEntry }]);
        toast.success("Entry added successfully");
      } else {
        // Updating existing entry
        const updatedRecords = [...childrecords];
        updatedRecords[editIndex] = newEntry;
        setchildRecords(updatedRecords);
        setEditIndex(null);
        if (typeof selectedchildId === "function") selectedchildId(null);
        toast.success("Entry updated successfully");
      }

      resetForm();

    } catch (err) {
      console.error("Error in handleAddOrUpdateEntry:", err);
      setWarningMessage("Unexpected error while processing entry");
      setWarningVisible(true);
    } finally {
      setLoading(false);
    }
  };


  const handleEditEntry = (id) => {
    // Find the entry by id
    const entryIndex = childrecords.findIndex((r) => String(r.id) === String(id));
    if (entryIndex === -1) return; // Not found

    const entry = childrecords[entryIndex];

    // Update formData with primitives only
    updateFormData("material", String(entry.material_master_id || ""));
    updateFormData("weight", String(entry.weight || ""));
    updateFormData("invoiceValue", String(entry.invoice_amount_usd || ""));
    updateFormData("dollar", String(entry.dollar_inr || ""));
    updateFormData("price", String(entry.price || ""));
    updateFormData("duty", String(entry.duty_inbond_amount_inr || ""));
    updateFormData("assessment", String(entry.assessment_amount_inr || ""));
    updateFormData("totalAmount", String(entry.material_amount_usd || ""));
    updateFormData("id", String(entry.id || ""));

    // Store the index internally for updates
    setEditIndex(entryIndex);
  };


  const resetForm = () => {
    updateFormData("material", "");
    updateFormData("weight", "");
    updateFormData("invoiceValue", "");
    updateFormData("dollar", "");
    updateFormData("price", "");
    updateFormData("duty", "");
    updateFormData("assessment", "");
    updateFormData("totalAmount", "");
    updateFormData("id");
    setEditIndex(null);
  };

  // === Safe rendering helpers ===
  const renderValue = (val) => {
    const primitive = safePrimitive(val);
    return primitive || "";
  };

  const formatIndianNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return "0";
    return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };


  // === Render ===
  return (
    <>
      <div className="space-y-4 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-6 items-start">

          {/* LEFT SIDE FORM */}
          <div className="lg:col-span-4 col-span-12 bg-white shadow-md border border-gray-100 rounded p-5 space-y-4">

            {/* BI + BE Inputs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <LabelInput
                label="BI No."
                type="text"
                value={renderValue(formData.biNumber)}
                onChange={handleBiChange}
                loading={loadingBINumber}
                required
                disabled={!isChildEdit}
              />

              <LabelInput
                label="BE No."
                type="text"
                value={renderValue(formData.beNumber)}
                onChange={handleBeNumberChange}
                required
                disabled={!isChildEdit}
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DateLabelInput
                label="BE Date"
                type="date"
                value={renderValue(formData.beDate)}
                onChange={handleBeDateChange}
                required
                disabled={!isChildEdit}
              />

              <DateLabelInput
                label="In-bond Date"
                type="date"
                value={renderValue(formData.inbondDate)}
                onChange={handleInbondDateChange}
                required
                min={renderValue(formData.beDate)}
                disabled={!isChildEdit}
              />
            </div>

            <div className="py-1.5 border-t border-dashed border-gray-300 w-full"></div>

            <h2 className="text-sm sm:text-base font-semibold text-gray-800 flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              Material & Financial Details
            </h2>

            {/* Material + Weight */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SelectMenu
                label="Material"
                options={materialList}
                value={formData.material}     // PRIMITIVE ONLY
                onChange={(val) => updateFormData("material", val)}
                searchable
                required
                loading={loadingMaterials}
                usePortal
              />


              <LabelInput
                label="Weight (kg)"
                type="number"
                value={renderValue(formData.weight)}
                onChange={makeNumericHandler("weight")}
                required
              />
            </div>

            {/* Invoice + ER */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <LabelInput
                label="Invoice Value ($)"
                type="number"
                value={renderValue(formData.invoiceValue)}
                onChange={makeNumericHandler("invoiceValue")}
                required
              />

              <LabelInput
                label="Dollar (₹) (ER)"
                type="number"
                value={renderValue(formData.dollar)}
                onChange={makeNumericHandler("dollar")}
              />
            </div>

            {/* Price + Duty */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <LabelInput
                label="Price"
                type="number"
                value={renderValue(formData.price)}
                onChange={makeNumericHandler("price")}
              />

              <LabelInput
                label="Duty In-bond (₹)"
                type="number"
                value={renderValue(formData.duty)}
                onChange={makeNumericHandler("duty")}
                required
              />
            </div>

            {/* Assessment + Total */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <LabelInput
                label="Assessment Value (₹)"
                type="number"
                value={renderValue(formData.assessment)}
                onChange={makeNumericHandler("assessment")}
                required
              />

              <LabelInput
                label="Total Amount material ($)"
                type="text"
                value={
                  formData.weight && formData.invoiceValue
                    ? (toNumber(formData.weight) * toNumber(formData.invoiceValue)).toLocaleString()
                    : ""
                }
                readOnly
              />
            </div>

            {/* Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                className={`w-full h-8 text-xs font-medium px-3 ${!canAddEntry ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={handleAddOrUpdateEntry}
                disabled={!canAddEntry}
                loading={loading}
                title={selectedchildId ? "Finish editing the selected entry before adding a new one" : ""}
              >
                {editIndex !== null ? "Update Entry" : "Add Entry"}
              </Button>


              <CancelButton onClick={resetForm} type="button" className="w-full sm:w-auto">
                Reset
              </CancelButton>
            </div>
          </div>

          {/* RIGHT SIDE TABLE */}
          <div className="lg:col-span-8 col-span-12 space-y-4">
            {childrecords && childrecords.length > 0 ? (
              <>
                {/* Summary Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full text-[12px] sm:text-[13px] text-gray-700">
                    <tbody>
                      {[
                        ["BI Number", `${renderValue(formData.biNumber)}`, "BE Number", renderValue(formData.beNumber)],
                        ["BE Date", formatDate(renderValue(formData.beDate)), "In-bond Date", formatDate(renderValue(formData.inbondDate))],
                        ["Total Duty In-bond", formatIndianNumber(totalDuty.toFixed(2)), "Total Weight", formatIndianNumber(totalWeight.toFixed(2))],
                        ["Total Assessment Value", formatIndianNumber(totalAssessment.toFixed(2)), "Total Amount of Material", formatIndianNumber(totalAmount.toFixed(2))],
                      ].map((row, i) => (
                        <tr key={i} className="border-b border-gray-200 bg-white">
                          <td className="px-2 py-2 text-gray-600 font-medium w-[25%]">{row[0]}</td>
                          <td className="px-2 py-2 font-semibold text-gray-900 w-[25%]">{row[1]}</td>
                          <td className="px-2 py-2 text-gray-600 font-medium w-[25%]">{row[2]}</td>
                          <td className="px-2 py-2 font-semibold text-gray-900 w-[25%]">{row[3]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Material Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-[700px] sm:min-w-full text-[11px] sm:text-[12px] text-center">
                    <thead className="h-10 border-b border-gray-200 text-gray-600 font-medium">
                      <tr>
                        <th>Material</th>
                        <th>Weight</th>
                        <th>Invoice Value</th>
                        <th>Dollar</th>
                        <th>Price</th>
                        <th>Duty In-bond</th>
                        <th>Assessment Value</th>
                        <th>Total Amount</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>

                      {(selectedchildId && selectedchildId !== 0
                        ? childrecords.filter(record => record.id === selectedchildId)
                        : childrecords
                      )
                        .map((entry, i) => (
                          <tr key={i} className="border-t h-10 border-gray-100 hover:bg-gray-50">
                            <td>{getShortCode(entry.material_master_id)}</td>
                            <td>{formatIndianNumber(Number(entry.weight || 0).toFixed(2))}</td>
                            <td>{formatIndianNumber(Number(entry.invoice_amount_usd || 0).toFixed(2))}</td>
                            <td>{formatIndianNumber(Number(entry.dollar_inr || 0).toFixed(2))}</td>
                            <td>{formatIndianNumber(Number(entry.price || 0).toFixed(2))}</td>
                            <td>{formatIndianNumber(Number(entry.duty_inbond_amount_inr || 0).toFixed(2))}</td>
                            <td>{formatIndianNumber(Number(entry.assessment_amount_inr || 0).toFixed(2))}</td>
                            <td>{formatIndianNumber(Number(entry.material_amount_usd || 0).toFixed(2))}</td>
                            <td>
                              <div className="flex items-center justify-center space-x-1">
                                <button
                                  id={entry.id}
                                  onClick={() => handleEditEntry(entry.id)}
                                  className="text-green-500 hover:text-green-700 p-1"
                                  title="Edit Entry"
                                >
                                  <MdEditSquare size={16} />
                                </button>

                                <button
                                  onClick={() => handleDeleteChild(entry)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                  title="Delete Entry"
                                >
                                  <RiDeleteBin5Line size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>

                  </table>
                </div>
              </>
            ) : (
              <NoFound />
            )}
          </div>
        </div>
      </div>
      {/* Delete Preview Modal */}
      {showDeletePreview && pendingDeleteData?.child && (
        <DeletePreviewModal
          title="Child Entry Details"
          data={{
            "Material": pendingDeleteData.child?.material_short_code.toUpperCase(),
            "Weight": pendingDeleteData.child?.weight,
            "Invoice Value": pendingDeleteData.child?.invoice_amount_usd,
            "Dollar": pendingDeleteData.child?.dollar_inr,
            "Duty In-bond": pendingDeleteData.child?.duty_inbond_amount_inr,
            "Assessment Value": pendingDeleteData.child?.assessment_amount_inr,
            "Total Amount": pendingDeleteData.child?.material_amount_usd,
          }}
          onCancel={handleCancelPreview}
          onProceed={handleProceedToFinalConfirm}
        />
      )}

      {/* Final Confirm Modal */}
      {showFinalDeleteConfirm && (
        <FinalDeleteConfirmModal
          title="Confirm Delete"
          message="Are you sure you want to permanently delete this entry?"
          onCancel={() => {
            setShowFinalDeleteConfirm(false);
            setPendingDeleteData(null);
            setDeleteMode(null);
          }}
          onConfirm={handleConfirmDelete}
        />
      )}
      {warningVisible && (
        <WarningModal
          message={warningMessage}
          onClose={() => setWarningVisible(false)}
        />
      )}

    </>
  );
}